import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {InternalNode} from "crqlar-react-common/dist/models/profile/internalNode.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";

// Marks a folio item whose VAT was explicitly set to 0% by staff (no tax rate exists for the
// service) so the Casablanca charge can carry a deliberate rate. Used to recognize + revert the stamp.
export const ZERO_VAT_NOTE_NAME = 'CASABLANCA_EXPLICIT_ZERO_VAT';
const ZERO_VAT_NOTE = 'Charged with explicit 0% VAT — no tax rate configured for this service; confirmed by staff.';

const isZeroVatNote = (note: InternalNode) => note.name === ZERO_VAT_NOTE_NAME;

// Untouched notes stay as they are (an empty list isn't the same as none to the draft)
const withoutZeroVatNote = (notes?: InternalNode[]) => (
    notes?.some(isZeroVatNote) ? notes.filter(note => !isZeroVatNote(note)) : notes
);

/** Staff charged this line with a deliberate 0 %: no VAT id, a 0 % snapshot and the audit note. */
export const hasZeroVatStamp = (line: FolioItem): boolean => (
    !line.taxRateId && line.taxRateSnap?.rate === 0 && !!line.notes?.some(isZeroVatNote)
);

/**
 * Casablanca books VAT exactly as transmitted and needs a rate on every line, so for a service
 * without a tax rate staff can send a real 0 % snapshot (the backend keeps a snapshot without an id)
 * together with an audit note.
 */
export const zeroVatPatch = (line: FolioItem): Partial<FolioItem> => ({
    taxRateId: undefined,
    taxRateSnap: {rate: 0, inclusive: false},
    notes: line.notes?.some(isZeroVatNote)
        ? line.notes
        : [...(line.notes ?? []), {name: ZERO_VAT_NOTE_NAME, note: ZERO_VAT_NOTE, new: true}],
});

export const removeZeroVatPatch = (line: FolioItem): Partial<FolioItem> => ({
    taxRateSnap: undefined,
    notes: withoutZeroVatNote(line.notes),
});

/**
 * A VAT rate picked for the line, or none: the id and its snapshot go together (the backend resolves
 * the snapshot from the id; without an id it keeps the one sent, so clearing sends none). A 0 % stamp
 * no longer applies either way.
 */
export const vatPatch = (line: FolioItem, taxRate?: TaxRate): Partial<FolioItem> => ({
    taxRateId: taxRate?.id,
    taxRateSnap: taxRate,
    notes: withoutZeroVatNote(line.notes),
});
